import api from '../../../utils/api';
import qs from 'querystringify';
import { staticFilter, stringifyObjectProps } from '../../../utils/filterHelper';
import { productsFilter } from '../../../constants/filters';
import moment from 'moment';
import { escapeSpecialChars } from '../../../utils/textHelper';

export const getSupplier = (supplierId) => {
  return api.get(`/suppliers/${supplierId}`);
};

export const putSupplier = (supplierId, data) => {
  return api.put(`/suppliers/${supplierId}`, data);
};

export const getSupplierRetailers = (
  supplierId,
  filter = null,
  limit = null,
  offset = null,
  order = null,
  sort = null,
) => {
  const url = `/suppliers/${supplierId}/retailers`;
  const params = {};

  if (filter) params.filter = JSON.stringify(filter);
  if (limit) params.limit = limit;
  if (offset) params.offset = offset;
  if (order) params.order = order;
  if (sort) params.sort = sort;

  return api.get(url + qs.stringify(params, true));
};

let cancelGetSupplierRetailersAll;
export const getSupplierRetailersAll = (supplierId, filter = null, order = null, sort = null) => {
  if (cancelGetSupplierRetailersAll) {
    cancelGetSupplierRetailersAll('Cancel');
  }

  const url = `/suppliers/${supplierId}/retailers-all`;
  const params = {};

  if (filter) params.filter = filter;
  if (order) params.order = order;
  if (sort) params.sort = sort;

  return api.get(url + qs.stringify(params, true), {
    cancelToken: new api.CancelToken((c) => (cancelGetSupplierRetailersAll = c)),
  });
};

let cancelGetSupplierRetailer;
export const getSupplierRetailer = (supplierId, id) => {
  if (cancelGetSupplierRetailer) {
    cancelGetSupplierRetailer('Cancel get retailer id');
  }

  return api.get(`/suppliers/${supplierId}/retailers/${id}`, {
    cancelToken: new api.CancelToken((c) => (cancelGetSupplierRetailer = c)),
  });
};

let cancelGetSupplierUsers;
export const getSupplierUsers = (supplierId) => {
  if (cancelGetSupplierUsers) {
    cancelGetSupplierUsers('Cancel get users');
  }

  return api.get(`/suppliers/${supplierId}/users?limit=200`, {
    cancelToken: new api.CancelToken((c) => (cancelGetSupplierUsers = c)),
  });
};

let cancelAddUser;
export const addUser = (supplierId, data) => {
  if (cancelAddUser) {
    cancelAddUser('Cancel add user');
  }

  return api.post(`/suppliers/${supplierId}/users`, data, {
    cancelAddUser: new api.CancelToken((c) => (cancelAddUser = c)),
  });
};

let cancelDeleteUser;
export const deleteUser = (supplierId, id) => {
  if (cancelDeleteUser) {
    cancelDeleteUser('Cancel delete user');
  }

  return api.delete(`/suppliers/${supplierId}/users/${id}`, {
    cancelToken: new api.CancelToken((c) => (cancelDeleteUser = c)),
  });
};

let cancelDeleteUserInvitation;
export const deleteUserInvitation = (supplierId, email) => {
  if (cancelDeleteUserInvitation) {
    cancelDeleteUserInvitation('Cancel delete user');
  }

  return api.delete(`/suppliers/${supplierId}/user_invitations/${email}`, {
    cancelToken: new api.CancelToken((c) => (cancelDeleteUserInvitation = c)),
  });
};

let cancelPutUserRetailers;
export const putUserRetailers = (supplierId, data) => {
  if (cancelPutUserRetailers) {
    cancelPutUserRetailers("Cancel updating supplier user's retailers");
  }

  return api.post(`/suppliers/${supplierId}/users`, data, {
    cancelPutUserRetailers: new api.CancelToken((c) => (cancelPutUserRetailers = c)),
  });
};

let cancelGetProductFilters;
export const getProductFilters = (supplierId, service, retailerId, productServiceEnabled) => {
  if (cancelGetProductFilters) {
    cancelGetProductFilters('Cancel get product-filters');
  }

  if (productServiceEnabled) {
    const url = `/suppliers/${supplierId}/product-filters-service`;
    return api.get(url, {
      cancelToken: new api.CancelToken((c) => (cancelGetProductFilters = c)),
    });
  }

  const url = `/suppliers/${supplierId}/product-filters`;
  const params = {};

  if (service) {
    params.permissions_for_service = service;
  }

  if (retailerId) {
    params.permissions_for_retailer_id = retailerId;
  }

  return api.get(url + qs.stringify(params, true), {
    cancelToken: new api.CancelToken((c) => (cancelGetProductFilters = c)),
  });
};

let cancelGetProducts;
export const getProducts = async (
  supplierId,
  currentFilter,
  offset,
  perPage,
  search,
  order,
  field,
  service,
  retailerId,
  variantServiceEnabled,
  additionalFilters = {},
) => {
  if (cancelGetProducts) {
    cancelGetProducts('Cancel get products');
  }

  // Check for valid orderBy field
  if (!productsFilter.find((filter) => filter.value === field)) {
    field = productsFilter[0].value;
  }
  const url = `/suppliers/${supplierId}/products`;

  const staticFilters = staticFilter(currentFilter, additionalFilters);
  const filters = variantServiceEnabled ? staticFilters : stringifyObjectProps(staticFilters);

  const params = {
    properties_filter: filters.propertiesFilter,
    filter: filters.filter,
    offset,
    limit: perPage,
    q: escapeSpecialChars(search),
    order,
    sort: field,
  };

  if (service) {
    params.permissions_for_service = service;
  }

  if (retailerId) {
    params.permissions_for_retailer_id = retailerId;
  }
  if (variantServiceEnabled) {
    const serviceUrl = `${url}-service`;
    const productsTotalUrl = `${url}-total`;
    const res = await api.post(serviceUrl, params, {
      cancelToken: new api.CancelToken((c) => (cancelGetProducts = c)),
    });
    try {
      const { propertiesFilter, filter } = stringifyObjectProps(staticFilters);
      params.filter = filter;
      params.properties_filter = propertiesFilter;

      const totalCount = await api.get(productsTotalUrl + qs.stringify(params, true), {
        cancelToken: new api.CancelToken((c) => (cancelGetProducts = c)),
      });
      res.headers['x-total-count'] = totalCount.data.total;
    } catch (e) {
      res.headers['x-total-count'] = 0;
    }
    return res;
  }
  return api.get(url + qs.stringify(params, true), {
    cancelToken: new api.CancelToken((c) => (cancelGetProducts = c)),
  });
};

let cancelGetProductsAll;
export const getProductsAll = (supplierId, currentFilter, search, service, retailerId, additionalFilters = {}) => {
  if (cancelGetProductsAll) {
    cancelGetProductsAll('Cancel products all');
  }

  const filters = stringifyObjectProps(staticFilter(currentFilter, additionalFilters));
  const url = `/suppliers/${supplierId}/products-all`;
  const params = {
    properties_filter: filters.propertiesFilter,
    filter: filters.filter,
    q: escapeSpecialChars(search),
  };

  if (service) {
    params.permissions_for_service = service;
  }

  if (retailerId) {
    params.permissions_for_retailer_id = retailerId;
  }

  return api.get(url + qs.stringify(params, true), {
    cancelToken: new api.CancelToken((c) => (cancelGetProductsAll = c)),
  });
};

let cancelGetSupplierWarehouses;
export const getSupplierWarehouses = async (supplierId) => {
  if (cancelGetSupplierWarehouses) {
    cancelGetSupplierWarehouses('Cancel get warehouses');
  }

  return api.get(`/suppliers/${supplierId}/warehouses`, {
    cancelToken: new api.CancelToken((c) => (cancelGetSupplierWarehouses = c)),
  });
};

let cancelGetInventory;
export const getInventory = (
  supplierId,
  currentFilter,
  offset,
  perPage,
  search,
  order,
  sortField,
  additionalFilters = {},
  fields = null,
) => {
  if (cancelGetInventory) {
    cancelGetInventory('Cancel get variants');
  }

  const filters = stringifyObjectProps(staticFilter(currentFilter, additionalFilters));
  const url = `/suppliers/${supplierId}/variants`;
  const params = {
    offset,
    limit: perPage,
    q: escapeSpecialChars(search),
    order,
    sort: sortField,
  };

  if (filters.propertiesFilter !== '{}') {
    params.properties_filter = filters.propertiesFilter;
  }

  if (filters.filter !== '{}') {
    params.filter = filters.filter;
  }

  if (fields) {
    params.fields = fields;
  }

  return api.get(url + qs.stringify(params, true), {
    cancelToken: new api.CancelToken((c) => (cancelGetInventory = c)),
  });
};

let cancelGetVariantHistory;
export const getVariantHistory = (variantId, offset, limit) => {
  if (cancelGetVariantHistory) {
    cancelGetVariantHistory('Cancel get variant history');
  }

  const filter = {
    created_at: {
      $lt: moment.utc(),
    },
  };

  const url = `/variants/${variantId}/history`;
  const params = {
    filter: JSON.stringify(filter),
    sort: 'created_at',
    order: 'desc',
  };

  if (offset) {
    params.offset = offset;
  }

  if (limit) {
    params.limit = limit;
  }

  return api.get(url + qs.stringify(params, true), {
    cancelToken: new api.CancelToken((c) => (cancelGetVariantHistory = c)),
  });
};
